import { useState, useEffect } from "react";
import {
  X,
  ChevronDown,
  Image,
  DollarSign,
  Tags,
  BookOpen,
  ChevronRight,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/admin/ui/Button";
import { Input } from "@/components/admin/ui/Input";
import { Select } from "@/components/admin/ui/Select";
import { Card } from "@/components/admin/ui/Card";
import { useToast } from "@/components/admin/ui/Toast";
import logo from "@/assets/logo.png";

interface BundleBuilderProps {
  onClose: () => void;
  bundleId?: number | null;
  onSaveSuccess?: (id: number) => void;
}

const AccordionItem = ({
  title,
  icon: Icon,
  open,
  onToggle,
  children,
}: {
  title: string;
  icon: React.ElementType;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) => (
  <div className="border-b border-gray-100 dark:border-white/5 last:border-0">
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center justify-between px-5 py-3.5 text-left hover:bg-gray-50/50 dark:hover:bg-white/3 transition-colors shadow-none border-none outline-none cursor-pointer"
    >
      <div className="flex items-center gap-2.5 text-gray-700 dark:text-[#D1D5DB]">
        <Icon className="h-4 w-4 text-gray-500 dark:text-[#A0A5AD]" />
        <span className="text-sm font-medium">{title}</span>
      </div>
      <ChevronDown
        className={`h-4 w-4 text-gray-400 dark:text-[#80858D] transition-transform transform ${
          open ? "rotate-180" : ""
        }`}
      />
    </button>
    {open && <div className="px-5 pb-4">{children}</div>}
  </div>
);

export function BundleBuilder({ onClose, bundleId, onSaveSuccess }: BundleBuilderProps) {
  const [step, setStep] = useState<"basics" | "additional">("basics");
  const { showToast } = useToast();

  // Form States
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [thumbnailUrl, setThumbnailUrl] = useState("");
  const [thumbnailId, setThumbnailId] = useState<number | null>(null);
  const [regularPrice, setRegularPrice] = useState("149.99");
  const [salePrice, setSalePrice] = useState("99.99");
  const [visibility, setVisibility] = useState("public");
  const [whatWillILearn, setWhatWillILearn] = useState("");

  // Taxonomy & Search States
  const [dbCategories, setDbCategories] = useState<any[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [selectedCourses, setSelectedCourses] = useState<any[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [allDbCourses, setAllDbCourses] = useState<any[]>([]);
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);
  const [isLoadingSelectorCourses, setIsLoadingSelectorCourses] = useState(false);

  // Accordion state
  const [accordionOpen, setAccordionOpen] = useState({
    visibility: true,
    featured: true,
    pricing: true,
    categories: true,
  });

  const toggleAccordion = (key: keyof typeof accordionOpen) => {
    setAccordionOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Fetch Categories from course_batch_category taxonomy
  useEffect(() => {
    const ajaxUrl =
      (window as any).shLmsAdmin?.ajax_url ||
      (window as any).hubInstructor?.ajax_url ||
      (window as any).ajaxurl;
    const adminNonce =
      (window as any).shLmsAdmin?.nonce ||
      (window as any).chorchaAdmin?.nonce ||
      (window as any).ajaxurl;

    if (!ajaxUrl || !(window as any).jQuery) return;

    (window as any).jQuery.ajax({
      url: ajaxUrl,
      method: "POST",
      data: {
        action: "chorcha_get_course_terms",
        nonce: adminNonce,
        taxonomy: "course_batch_category",
      },
      success: (res: any) => {
        if (res.success && Array.isArray(res.data)) {
          setDbCategories(res.data);
        }
      },
      error: (err: any) => {
        console.error("Failed to fetch categories:", err);
      },
    });
  }, []);

  // Hydrate form states if bundleId is provided (Edit mode)
  useEffect(() => {
    if (!bundleId) return;

    const ajaxUrl =
      (window as any).shLmsAdmin?.ajax_url ||
      (window as any).hubInstructor?.ajax_url ||
      (window as any).ajaxurl;
    const adminNonce =
      (window as any).shLmsAdmin?.nonce ||
      (window as any).chorchaAdmin?.nonce ||
      (window as any).ajaxurl;

    if (!ajaxUrl || !(window as any).jQuery) return;

    (window as any).jQuery.ajax({
      url: ajaxUrl,
      method: "POST",
      data: {
        action: "chorcha_get_bundle",
        nonce: adminNonce,
        post_id: bundleId,
      },
      success: (res: any) => {
        if (res.success && res.data) {
          const b = res.data;
          setTitle(b.title || "");
          setUrl(b.url || "");
          setDescription(b.description || "");
          setThumbnailUrl(b.thumbnail_url || "");
          setThumbnailId(b.thumbnail_id ? Number(b.thumbnail_id) : null);
          setRegularPrice(b.regular_price ? b.regular_price.toString() : "0");
          setSalePrice(b.sale_price ? b.sale_price.toString() : "0");
          setVisibility(b.status === "publish" ? "public" : "private");
          setWhatWillILearn(b.what_will_i_learn || "");
          setSelectedCategories(b.categories ? b.categories.map(Number) : []);
          setSelectedCourses(b.courses || []);
        }
      },
      error: (err: any) => {
        console.error("Failed to load bundle details:", err);
      },
    });
  }, [bundleId]);



  const fetchAllDbCourses = () => {
    const ajaxUrl =
      (window as any).shLmsAdmin?.ajax_url ||
      (window as any).hubInstructor?.ajax_url ||
      (window as any).ajaxurl;
    const adminNonce =
      (window as any).shLmsAdmin?.nonce ||
      (window as any).chorchaAdmin?.nonce ||
      (window as any).ajaxurl;

    if (!ajaxUrl || !(window as any).jQuery) return;

    setIsLoadingSelectorCourses(true);
    (window as any).jQuery.ajax({
      url: ajaxUrl,
      method: "POST",
      data: {
        action: "chorcha_search_courses",
        nonce: adminNonce,
        search: "",
      },
      success: (res: any) => {
        setIsLoadingSelectorCourses(false);
        if (res.success && Array.isArray(res.data)) {
          setAllDbCourses(res.data);
        }
      },
      error: () => {
        setIsLoadingSelectorCourses(false);
      },
    });
  };

  const handleSelectCourse = (course: any) => {
    setSelectedCourses((prev) => [...prev, course]);
  };

  const handleRemoveCourse = (courseId: number) => {
    setSelectedCourses((prev) => prev.filter((c) => c.id !== courseId));
  };

  // Open WP Media Library for Featured Image
  const openMediaLibrary = () => {
    const wp = (window as any).wp;
    if (wp && wp.media) {
      const frame = wp.media({
        title: "Select or Upload Featured Image",
        button: {
          text: "Use this image",
        },
        multiple: false,
      });

      frame.on("select", () => {
        const attachment = frame.state().get("selection").first().toJSON();
        setThumbnailUrl(attachment.url);
        setThumbnailId(attachment.id);
      });

      frame.open();
    } else {
      alert(
        "WordPress Media Library is not loaded. Please ensure wp_enqueue_media() was executed in PHP."
      );
    }
  };

  // Auto generate slug from title
  const handleTitleChange = (val: string) => {
    setTitle(val);
    const autoSlug = val
      .toLowerCase()
      .replace(/[^a-z0-9\u0980-\u09ff]+/g, "-") // support both English and Bangla characters in slug if needed
      .replace(/(^-|-$)/g, "");
    setUrl(autoSlug);
  };

  // Save / Publish handlers
  const handleSave = (status: "publish" | "draft") => {
    if (!title.trim()) {
      showToast("Please enter a bundle title", "error");
      return;
    }

    const ajaxUrl =
      (window as any).shLmsAdmin?.ajax_url ||
      (window as any).hubInstructor?.ajax_url ||
      (window as any).ajaxurl;
    const adminNonce =
      (window as any).shLmsAdmin?.nonce ||
      (window as any).chorchaAdmin?.nonce ||
      (window as any).ajaxurl;

    if (!ajaxUrl || !(window as any).jQuery) {
      showToast("WordPress AJAX endpoint not found", "error");
      return;
    }

    setIsSaving(true);
    showToast(
      status === "publish"
        ? "Publishing Course Bundle..."
        : "Saving Course Bundle as Draft...",
      "info"
    );

    (window as any).jQuery.ajax({
      url: ajaxUrl,
      method: "POST",
      data: {
        action: "chorcha_save_bundle",
        nonce: adminNonce,
        post_id: bundleId || "",
        title: title.trim(),
        url: url.trim(),
        description: description.trim(),
        status: status,
        regular_price: regularPrice,
        sale_price: salePrice,
        thumbnail_id: thumbnailId || "",
        categories: selectedCategories,
        course_ids: selectedCourses.map((c) => c.id),
        what_will_i_learn: whatWillILearn,
      },
      success: (res: any) => {
        setIsSaving(false);
        if (res.success) {
          showToast(res.data.message || "Bundle saved successfully!", "success");
          if (res.data.post_id && onSaveSuccess) {
            onSaveSuccess(Number(res.data.post_id));
          }
        } else {
          showToast(res.data || "Failed to save bundle", "error");
        }
      },
      error: (err: any) => {
        setIsSaving(false);
        console.error("Save Bundle Error:", err);
        showToast("An error occurred while saving the bundle", "error");
      },
    });
  };

  return (
    <div className="flex-1 flex flex-col bg-gray-50 transition-colors h-full overflow-hidden w-full">
      {/* Header */}
      <header className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img
            src={logo}
            alt="LMS"
            className="h-9 w-9 rounded-lg object-cover"
          />
          <span className="font-semibold text-gray-800">Course Bundle</span>
        </div>
        <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg">
          {[
            { key: "basics", label: "Basics", num: 1 },
            { key: "additional", label: "Additional", num: 2 },
          ].map((s) => (
            <button
              key={s.key}
              onClick={() => setStep(s.key as typeof step)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                step === s.key
                  ? "bg-white text-[#ff0000] shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <span className="mr-1.5">{s.num}.</span>
              {s.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleSave("draft")}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save as Draft"}
          </Button>
          <Button size="sm" onClick={() => handleSave("publish")} disabled={isSaving}>
            {isSaving ? "Publishing..." : "Publish"}
          </Button>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-6 lg:p-8 pb-20">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6">
          {step === "basics" && (
            <>
              <div className="lg:col-span-8 space-y-6">
                <Card>
                  <Input
                    label="Bundle Title"
                    placeholder="Enter bundle title"
                    value={title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                  />
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-[#D1D5DB] mb-1.5">
                      Bundle URL
                    </label>
                    <div className="sh-light-box flex items-center rounded-lg px-3 py-2 text-sm">
                      <span>
                        {(window as any).shLmsAdmin?.home_url ||
                          "https://lms.example.com/"}
                        bundle/
                      </span>
                      <input
                        type="text"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        placeholder="web-development-bundle"
                        className="ml-1 flex-1 font-semibold"
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-[#D1D5DB] mb-1.5">
                      Description
                    </label>
                    <div className="sh-light-editor border border-gray-200 dark:border-white/10 rounded-lg overflow-hidden">
                      <div className="editor-toolbar flex items-center gap-1 border-b border-gray-100 bg-gray-50 px-3 py-2">
                        {["B", "I", "U", "S", "Link", "Image"].map((tool) => (
                          <button
                            key={tool}
                            type="button"
                            className="px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-200 rounded transition-colors"
                          >
                            {tool}
                          </button>
                        ))}
                      </div>
                      <textarea
                        rows={6}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Describe your course bundle..."
                        className="w-full p-4 text-sm focus:outline-none resize-none border-none"
                      />
                    </div>
                  </div>
                </Card>

                <Card
                  title="Courses"
                  description="Select courses to include in this bundle"
                >
                  <div className="mb-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                          Selected Courses
                        </h4>
                        <p className="text-[11px] text-[#5A6A85] mt-0.5">
                          Courses bundled inside this package
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        leftIcon={<BookOpen className="h-4.5 w-4.5" />}
                        onClick={() => {
                          fetchAllDbCourses();
                          setIsSelectorOpen(true);
                        }}
                      >
                        + Add Courses
                      </Button>
                    </div>
                  </div>

                  {selectedCourses.length === 0 ? (
                    <div className="border-2 border-dashed border-gray-200 dark:border-white/10 rounded-xl p-8 text-center bg-gray-50/50 dark:bg-white/3">
                      <div className="h-14 w-14 rounded-full bg-red-50 dark:bg-[#ff0000]/10 flex items-center justify-center mx-auto mb-3">
                        <BookOpen className="h-6 w-6 text-[#ff0000]" />
                      </div>
                      <h4 className="text-base font-semibold text-gray-800 dark:text-[#E8EAED]">
                        No courses added yet
                      </h4>
                      <p className="text-sm text-gray-500 dark:text-[#A0A5AD] mt-1 max-w-sm mx-auto">
                        Add existing courses to create a bundle. Students will
                        get access to all selected courses.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {selectedCourses.map((course) => (
                        <div
                          key={course.id}
                          className="flex items-center justify-between rounded-xl border border-gray-200 dark:border-white/5 bg-white dark:bg-[#1e293b] p-4 shadow-sm hover:shadow transition-all"
                        >
                          <div className="flex items-center gap-4 min-w-0">
                            {course.thumbnail && !course.thumbnail.includes("544716278-ca5e3f4abd8c") ? (
                              <img
                                src={course.thumbnail}
                                alt={course.title}
                                className="h-12 w-16 rounded-lg object-cover flex-shrink-0 border border-gray-100"
                              />
                            ) : (
                              <div className="h-12 w-16 rounded-lg bg-gray-50 border border-gray-200 flex items-center justify-center flex-shrink-0 text-gray-400">
                                <BookOpen className="h-5 w-5" />
                              </div>
                            )}
                            <div className="min-w-0">
                              <h5 className="text-sm font-semibold text-gray-800 dark:text-[#E8EAED] leading-snug">
                                {course.title}
                              </h5>
                              <div className="mt-1.5">
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-[#ff0000]/8 text-[#ff0000]">
                                  {course.category}
                                </span>
                              </div>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveCourse(course.id)}
                            className="text-xs font-semibold text-red-600 hover:text-red-700 dark:text-[#ff4444] dark:hover:text-[#ff6666] flex items-center gap-1.5 transition-colors p-2 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg cursor-pointer"
                          >
                            <Trash2 className="h-4 w-4" />
                            <span>Remove</span>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </div>

              <div className="lg:col-span-4 space-y-6">
                <Card className="divide-y divide-gray-100 dark:divide-white/5">
                  <AccordionItem
                    title="Visibility"
                    icon={BookOpen}
                    open={accordionOpen.visibility}
                    onToggle={() => toggleAccordion("visibility")}
                  >
                    <Select
                      options={[
                        { value: "public", label: "Public" },
                        { value: "private", label: "Private" },
                      ]}
                      value={visibility}
                      onChange={(val) => setVisibility(val)}
                    />
                  </AccordionItem>
                  <AccordionItem
                    title="Featured Image"
                    icon={Image}
                    open={accordionOpen.featured}
                    onToggle={() => toggleAccordion("featured")}
                  >
                    {thumbnailUrl ? (
                      <div className="relative group rounded-lg overflow-hidden border border-gray-200 dark:border-white/10">
                        <img
                          src={thumbnailUrl}
                          alt="Featured preview"
                          className="w-full h-40 object-cover"
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <button
                            type="button"
                            onClick={openMediaLibrary}
                            className="px-3 py-1.5 bg-white text-gray-800 rounded-md hover:bg-gray-100 text-xs font-semibold shadow-sm transition-colors"
                          >
                            Change
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setThumbnailUrl("");
                              setThumbnailId(null);
                            }}
                            className="px-3 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 text-xs font-semibold shadow-sm transition-colors"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        onClick={openMediaLibrary}
                        className="border-2 border-dashed border-gray-200 dark:border-white/10 rounded-lg p-6 text-center hover:border-[#ff0000]/30 dark:hover:border-[#ff4444]/30 transition-colors cursor-pointer"
                      >
                        <Image className="h-8 w-8 text-gray-300 dark:text-[#80858D] mx-auto mb-2" />
                        <p className="text-sm text-gray-500 dark:text-[#A0A5AD]">
                          Click to upload featured image
                        </p>
                      </div>
                    )}
                  </AccordionItem>
                  <AccordionItem
                    title="Pricing"
                    icon={DollarSign}
                    open={accordionOpen.pricing}
                    onToggle={() => toggleAccordion("pricing")}
                  >
                    <div className="space-y-3">
                      <Input
                        label="Regular Price ($)"
                        type="text"
                        value={regularPrice}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9.]/g, "");
                          const parts = val.split(".");
                          setRegularPrice(parts.length > 2 ? parts[0] + "." + parts.slice(1).join("") : val);
                        }}
                      />
                      <Input
                        label="Sale Price ($)"
                        type="text"
                        value={salePrice}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9.]/g, "");
                          const parts = val.split(".");
                          setSalePrice(parts.length > 2 ? parts[0] + "." + parts.slice(1).join("") : val);
                        }}
                      />
                    </div>
                  </AccordionItem>
                  <AccordionItem
                    title="Categories"
                    icon={Tags}
                    open={accordionOpen.categories}
                    onToggle={() => toggleAccordion("categories")}
                  >
                    <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                      {dbCategories.length > 0 ? (
                        dbCategories.map((cat) => (
                          <label
                            key={cat.term_id}
                            className="flex items-center gap-2.5 text-sm text-gray-750 dark:text-[#D1D5DB] cursor-pointer hover:text-[#ff0000] dark:hover:text-[#ff4444] transition-colors py-1.5"
                          >
                            <input
                              type="checkbox"
                              checked={selectedCategories.includes(Number(cat.term_id))}
                              onChange={(e) => {
                                const termId = Number(cat.term_id);
                                if (e.target.checked) {
                                  setSelectedCategories((prev) => [
                                    ...prev,
                                    termId,
                                  ]);
                                } else {
                                  setSelectedCategories((prev) =>
                                    prev.filter((id) => id !== termId)
                                  );
                                }
                              }}
                              className="rounded border border-gray-300 bg-white text-[#ff0000] focus:ring-[#ff0000]/30 dark:bg-white/5 dark:border-white/20 h-4 w-4 cursor-pointer"
                            />
                            <span className="select-none font-medium text-gray-700 dark:text-[#D1D5DB]">{cat.name}</span>
                          </label>
                        ))
                      ) : (
                        <p className="text-xs text-gray-400 italic">
                          No categories found
                        </p>
                      )}
                    </div>
                  </AccordionItem>
                </Card>
              </div>
            </>
          )}

          {step === "additional" && (
            <div className="lg:col-span-12 animate-in fade-in duration-200">
              <Card title="Additional Information" className="max-w-4xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-[#D1D5DB] mb-1.5">
                      What Will I Learn?
                    </label>
                    <textarea
                      rows={8}
                      value={whatWillILearn}
                      onChange={(e) => setWhatWillILearn(e.target.value)}
                      placeholder="List the key benefits and learning outcomes..."
                      className="w-full rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-[rgba(20,20,20,0.6)] px-3 py-2 text-sm text-gray-700 dark:text-[#E8EAED] placeholder:text-gray-400 dark:placeholder:text-[#6b7280] focus:border-[#ff0000] focus:outline-none focus:ring-2 focus:ring-[#ff0000]/10"
                    />
                  </div>
                  <div className="lg:col-span-1">
                    <div className="bg-red-50 dark:bg-[#ff0000]/5 border border-red-100 dark:border-[#ff0000]/15 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-red-800 dark:text-[#ff6666] mb-2">
                        Bundle Tips
                      </h4>
                      <ul className="space-y-2 text-xs text-red-700 dark:text-[#ff8888]">
                        <li className="flex items-start gap-2">
                          <ChevronRight className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                          Group related courses for better value
                        </li>
                        <li className="flex items-start gap-2">
                          <ChevronRight className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                          Highlight total savings clearly
                        </li>
                        <li className="flex items-start gap-2">
                          <ChevronRight className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                          Use benefit-focused descriptions
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          )}
        </div>
      </main>

      {isSelectorOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-white/10 rounded-2xl w-full max-w-lg shadow-2xl flex flex-col max-h-[80vh] overflow-hidden animate-in zoom-in-95 duration-200">
            
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-white/5">
              <div>
                <h3 className="font-bold text-gray-800 dark:text-[#E8EAED] text-base">Select Courses</h3>
                <p className="text-xs text-gray-500 dark:text-[#A0A5AD] mt-0.5">Choose courses to include in this bundle</p>
              </div>
              <button
                onClick={() => setIsSelectorOpen(false)}
                className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {isLoadingSelectorCourses ? (
                <div className="py-12 text-center text-sm text-gray-500 italic">
                  Loading courses...
                </div>
              ) : allDbCourses.length === 0 ? (
                <div className="py-12 text-center text-sm text-gray-500 italic">
                  No published courses found.
                </div>
              ) : (
                allDbCourses.map((course) => {
                  const isSelected = selectedCourses.some((sc) => sc.id === course.id);
                  return (
                    <div
                      key={course.id}
                      onClick={() => {
                        if (isSelected) {
                          handleRemoveCourse(course.id);
                        } else {
                          handleSelectCourse(course);
                        }
                      }}
                      className={`flex items-center justify-between p-3 rounded-xl border transition-all cursor-pointer ${
                        isSelected
                          ? "border-red-500/30 bg-red-50/50 dark:bg-red-500/5 dark:border-red-500/20"
                          : "border-gray-100 dark:border-white/5 bg-white dark:bg-[#1a1f2c] hover:bg-gray-50 dark:hover:bg-white/5"
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        {course.thumbnail && !course.thumbnail.includes("544716278-ca5e3f4abd8c") ? (
                          <img
                            src={course.thumbnail}
                            alt={course.title}
                            className="h-10 w-14 rounded object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="h-10 w-14 rounded bg-gray-50 border border-gray-200 flex items-center justify-center flex-shrink-0 text-gray-400">
                            <BookOpen className="h-4 w-4" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-800 dark:text-[#E8EAED] truncate">
                            {course.title}
                          </p>
                          <div className="mt-1 flex items-center gap-2">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                              {course.category}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-center h-5 w-5 rounded border border-gray-300 dark:border-white/20 bg-white dark:bg-white/5 flex-shrink-0">
                        {isSelected && (
                          <span className="h-2.5 w-2.5 rounded-sm bg-red-600" />
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-100 dark:border-white/5 flex items-center justify-end bg-gray-50/50 dark:bg-white/1">
              <Button onClick={() => setIsSelectorOpen(false)} size="sm">
                Done
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
